import { NavLink, NavLinkProps } from 'react-router';
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle
} from '../ui/navigation-menu';
import { ReactNode } from 'react';
import { ThemeToggle } from '../theme/ThemeToggle';
import { ROUTES } from '@/constants';
import LoginButton from '../auth/LoginButton';
import { useAuth0 } from '@auth0/auth0-react';
import LogoutButton from '../auth/LogoutButton';
import { cn } from '@/lib/utils';
import { Role, useRoles } from '@/hooks/useRoles.ts';

type LinkProps = Omit<NavLinkProps, 'children'> & { children: ReactNode; isSublink?: boolean };

function Link({ children, className, isSublink, ...props }: LinkProps) {
	return (
		<NavLink {...props}>
			{({ isActive }) => (
				<NavigationMenuLink
					asChild
					active={isActive}
					className={cn(!isSublink && navigationMenuTriggerStyle(), isSublink && className)}
				>
					<span>{children}</span>
				</NavigationMenuLink>
			)}
		</NavLink>
	);
}

type NavMenuItem = {
	label: string;
	to?: string;
	description?: string;
	sublinks?: NavMenuItem[];
	requiredRoles?: Role[];
};

const pages: NavMenuItem[] = [
	{
		label: 'Devices',
		sublinks: [
			{
				label: 'Device dashboard',
				to: ROUTES.getDeviceURL('10'),
				description: 'Device stats and slow monitoring values',
				requiredRoles: [Role.ROLE_ANALYST]
			},
			{
				label: 'Deploy device',
				to: ROUTES.deployDevice,
				description: 'Deploy a new device',
				requiredRoles: [Role.ROLE_ANALYST]
			},
			{
				label: 'GPS Lock',
				to: ROUTES.gpsLockDevice,
				description: 'Check GPS lock of a device',
				requiredRoles: [Role.ROLE_CONTRACTOR]
			}
		]
	},
	{
		label: 'Measurement Groups',
		to: ROUTES.measurementGroups,
		requiredRoles: [Role.ROLE_ANALYST]
	},
	{
		label: 'Tasks',
		to: ROUTES.tasks,
		requiredRoles: [Role.ROLE_ANALYST]
	},
	{
		label: 'Process Objects',
		to: ROUTES.processObjects,
		requiredRoles: [Role.ROLE_ANALYST]
	},
	{
		label: 'Landowner',
		to: ROUTES.landowner,
		requiredRoles: [Role.ROLE_ANALYST]
	},
	{
		label: 'Field Reports',
		to: ROUTES.fieldReports,
		requiredRoles: [Role.ROLE_ANALYST]
	},
	{
		label: 'Map',
		to: ROUTES.map,
		requiredRoles: [Role.ROLE_ANALYST]
	}
];

type ListItemProps = {
	title: string;
	children: ReactNode;
	className?: string;
	href: string;
};

const ListItem = ({ className, title, children, ...props }: ListItemProps) => {
	return (
		<li className="text-nowrap">
			<Link
				{...props}
				isSublink
				to={props.href as string}
				className={cn(
					'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
					className
				)}
			>
				<span>
					<div className="text-sm font-medium leading-none">{title}</div>
					<p className="line-clamp-2 text-sm leading-snug text-muted-foreground">{children}</p>
				</span>
			</Link>
		</li>
	);
};

const MenuItemWithSublinks = (props: NavMenuItem) => {
	const { label, sublinks } = props;
	return (
		<NavigationMenuItem>
			<NavigationMenuTrigger>{label}</NavigationMenuTrigger>
			<NavigationMenuContent>
				<ul className="grid gap-3 p-1 w-fit">
					{sublinks &&
						sublinks.map(sublink => (
							<ListItem key={sublink.label} title={sublink.label} href={sublink.to as string}>
								{sublink.description}
							</ListItem>
						))}
				</ul>
			</NavigationMenuContent>
		</NavigationMenuItem>
	);
};

function Nav() {
	const { isAuthenticated } = useAuth0();
	const userRoles = useRoles();

	return (
		<NavigationMenu className="w-full max-w-full grow-0 px-10 py-2 shadow-sm">
			<NavigationMenuList>
				{pages
					.filter(p => {
						const hasAccess =
							(!p.requiredRoles || p.requiredRoles.some(role => userRoles.includes(role))) && !p.sublinks;
						const hasAccessibleSublinks = p.sublinks?.some(
							sublink =>
								!sublink.requiredRoles || sublink.requiredRoles.some(role => userRoles.includes(role))
						);
						return hasAccess || hasAccessibleSublinks;
					})
					.map(p => {
						if (p.sublinks) {
							const filteredSublinks = p.sublinks.filter(
								sublink =>
									!sublink.requiredRoles ||
									sublink.requiredRoles.some(role => userRoles.includes(role))
							);
							return <MenuItemWithSublinks key={p.label} {...p} sublinks={filteredSublinks} />;
						}
						return (
							<NavigationMenuItem key={p.label}>
								<Link to={p.to as string}>{p.label}</Link>
							</NavigationMenuItem>
						);
					})}
			</NavigationMenuList>

			<div className="ml-auto flex items-center gap-2">
				{isAuthenticated ? <LogoutButton /> : <LoginButton />}
				<ThemeToggle />
			</div>
		</NavigationMenu>
	);
}

export default Nav;
